import { useRef } from 'react';

import { Box, Center, Heading, Text } from '@chakra-ui/react';
import { useInView } from 'framer-motion';
import Image from 'next/image';

import { ParsedHTMLText } from '@/components/functional';
import { LetsBuildSomethingSection } from '@/components/sections/common';
import { InventoryDefinitionReadMore } from '@/components/sections/inventory/InventoryDefinitionReadMore/InventoryDefinitionReadMore';
import { InventoryDefinitionArticle } from '@/components/sections/inventory/typings';
import { SectionContentWrapper, SectionWrapper } from '@/components/wrappers';
import { ImageFragment } from '@/graphql/generated';
import { getImageAlt, getImageSrcFromAttributes } from '@/utils/images';

import { InventoryDefinitionBackground } from '../InventoryDefinitionBackground';
import { inventoryDefinitionStyles } from './inventory-definition-content-styles';

interface InventoryDefinitionContentProps {
  title: string;
  slug: string;
  content: string;
  articles?: InventoryDefinitionArticle[];
  doodle?: ImageFragment | null;
}

export const InventoryDefinitionContent = ({
  title,
  doodle,
  content,
  articles,
  slug,
}: InventoryDefinitionContentProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(containerRef);
  const shortTitle = slug.replaceAll('-', ' ');

  return (
    <SectionWrapper>
      <SectionContentWrapper
        ref={containerRef}
        maxW="846px"
        px={{
          base: 10,
          lg: '0 !important',
        }}
      >
        <InventoryDefinitionBackground isInView={isInView} />
        {doodle?.data ? (
          <Center mb="12">
            <Image
              src={getImageSrcFromAttributes(doodle.data?.attributes, 'medium')}
              alt={getImageAlt(doodle.data?.attributes, title)}
              loading="eager"
              width={250}
              height={250}
              style={{
                objectFit: 'contain',
                height: '250px',
              }}
            />
          </Center>
        ) : null}
        <Text fontWeight="normal" textTransform="capitalize" color="purple.500" textAlign="center">
          {shortTitle}
        </Text>
        <Heading
          as="h1"
          variant={{
            base: 'component-heading-large/xl',
            sm: 'component-heading-large/2xl',
            lg: 'component-heading-large/3xl',
          }}
          color="purple.500"
          textAlign="center"
          my="12"
        >
          {title}
        </Heading>
        <ParsedHTMLText content={content} innerStyles={inventoryDefinitionStyles} />
        {Array.isArray(articles) && articles.length > 0 ? <InventoryDefinitionReadMore articles={articles} /> : null}
      </SectionContentWrapper>
      <Box w="100%" overflow="hidden">
        <LetsBuildSomethingSection />
      </Box>
    </SectionWrapper>
  );
};
